import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSelectedBrandPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateByPath } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import BrandDetailsPopularProductsCard from "@sunSource/Components/BrandDetailsPopularProductsCard";
import loadBrandDetailsProducts from "@sunSource/Handlers/BrandDetails/LoadBrandDetailsProducts";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { pathname } = getLocation(state);
    const brandPath = getSelectedBrandPath(state) || pathname;
    const brandState = getBrandStateByPath(state, brandPath);
    const popularProducts = getProductsForProductInfoList(state, "brandDetailsPage")
    return {
        brandPath,
        brandState,
        popularProducts,
    };
};

const mapDispatchToProps = {
    loadBrandDetailsProducts,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface BrandDetailsPopularProductsStyles {
    container?: GridContainerProps;
    headerItem?: GridItemProps;
    headerText?: TypographyPresentationProps;
    bodyItem?: GridItemProps;
    cardContainer?: GridContainerProps;
    cardContainerItem?: GridItemProps;
    headerLink?: LinkPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const popularProductsStyles: BrandDetailsPopularProductsStyles = {
    container: {
        gap: 5,
        css: css`
        margin: 0;`,
    },
    headerItem: {
        width: 12,
        css:css`
            align-items: center;
        `
    },
    bodyItem: {
        width: 12,
    },
    cardContainer: {
        gap: 20,
        css: css`
        `,
    },
    cardContainerItem: {
        width: 12,
        css: css`
        gap: 25px 36px;
        flex-wrap: wrap;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    flex-direction: column;
                    align-items: center;
                `,
                null,
                null,
                null,
            ],
            "max",
        )}
    `,
    },
    headerText: {
        variant: "h1",
        css: css`
            width: 90%;
            font-size: 20px;
            overflow-wrap: break-word;
            word-wrap: break-word;
            color: #00539B;
            margin-bottom: 4px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        width: 75%;
                    `,
                    null,
                    null,
                    null,
                ],
                "max",
            )}
        `,
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 200px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = popularProductsStyles;

const BrandDetailsPopularProducts = ({
    brandState,
    popularProducts,
    loadBrandDetailsProducts,
}: Props) => {
    const [isLoading, setIsLoading] = useState(true);
    const currentBrandId = brandState?.value?.id;
    
    useEffect(() => {
        if (currentBrandId) {
            loadBrandDetailsProducts({brandId: currentBrandId})
        }
    }, [brandState.value?.productListPagePath]);
    
    useEffect(()=>{
        setIsLoading(true);
        setTimeout(()=>{
            setIsLoading(false);
        }, 1500);
    },[brandState.value?.productListPagePath])
    
    if (!popularProducts) {
        return null;
    }

    return (
        <GridContainer data-test-selector="popularProducts" {...styles.container}>
            <GridItem {...styles.headerItem}>
                <Typography {...styles.headerText}>
                    {translate("POPULAR PRODUCTS")}
                </Typography>
                <Link
                data-test-selector="brandToProductPageLink"
                href={brandState.value?.productListPagePath}
                {...styles.headerLink}
                >
                    {translate("VIEW ALL")}
                </Link>
            </GridItem>
            <GridItem {...styles.bodyItem}>
            {isLoading ? (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner}/>
                </StyledWrapper>
                ):(
                <GridContainer {...styles.cardContainer}>
                    <GridItem {...styles.cardContainerItem}>
                        {popularProducts?.slice(0, 6).map((product, index) => {
                            return(
                                <BrandDetailsPopularProductsCard ownId="brandDetailsPage" product={product} key={index}/>
                            )
                        })}
                    </GridItem>
                </GridContainer>
                )
            }
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandDetailsPopularProducts),
    definition: {
        group: "Brand Details",
        icon: "image",
        displayName: "Brand Popular Products",
        allowedContexts: ["BrandDetailsPage"],
    },
};

export default widgetModule;
